import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Download, Loader2, Infinity as InfinityIcon, AlertTriangle, CheckCircle2, Users } from 'lucide-react';
import { useEnterpriseSelfPaced, type SelfPacedInterestRow } from '@/hooks/useEnterpriseSelfPaced';

function fmtDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
}

function interestsToCsv(rows: SelfPacedInterestRow[]): string {
  const esc = (v: string) => `"${v.replace(/"/g, '""')}"`;
  const header = ['Nombre', 'Correo', 'Ruta', 'Fecha'];
  const lines = rows.map(r =>
    [r.full_name ?? '', r.email, r.program_name ?? '', new Date(r.created_at).toLocaleString('es-CO')]
      .map(esc)
      .join(','),
  );
  return [header.map(esc).join(','), ...lines].join('\n');
}

export function EnterpriseSelfPacedTab({ organizationId }: { organizationId: string | undefined }) {
  const { data, isLoading, error } = useEnterpriseSelfPaced(organizationId);

  if (!organizationId || organizationId === 'all') {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Selecciona una empresa para ver el avance en rutas de autoaprendizaje.
        </CardContent>
      </Card>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          No fue posible cargar la información de autoaprendizaje.
        </CardContent>
      </Card>
    );
  }

  const users = data?.users ?? [];
  const routes = data?.routes ?? [];
  const interests = data?.interests ?? [];

  const enrolled = users.length;
  const activeRoutes = routes.length;
  const avgProgress = enrolled
    ? Math.round(users.reduce((acc, u) => acc + (u.progress_pct ?? 0), 0) / enrolled)
    : 0;
  const behind = users.filter(u => (u.overdue_chapters ?? 0) > 0).length;
  const completedRoutes = users.filter(u => u.total_mandatory > 0 && u.done_mandatory === u.total_mandatory).length;

  const handleExportInterests = () => {
    const blob = new Blob(['\uFEFF' + interestsToCsv(interests)], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'interesados-del-equipo.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <Users className="h-4 w-4" /> Inscritos
            </div>
            <p className="text-2xl font-bold mt-2">{enrolled}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <InfinityIcon className="h-4 w-4" /> Rutas activas
            </div>
            <p className="text-2xl font-bold mt-2">{activeRoutes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="text-muted-foreground text-xs">Avance obligatorio promedio</div>
            <p className="text-2xl font-bold mt-2">{avgProgress}%</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <AlertTriangle className="h-4 w-4" /> Atrasados
            </div>
            <p className="text-2xl font-bold mt-2">{behind}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-muted-foreground text-xs">
              <CheckCircle2 className="h-4 w-4" /> Rutas completadas
            </div>
            <p className="text-2xl font-bold mt-2">{completedRoutes}</p>
          </CardContent>
        </Card>
      </div>

      {/* Por ruta */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Rutas de autoaprendizaje</CardTitle>
          <CardDescription>Inscritos y avance promedio por ruta</CardDescription>
        </CardHeader>
        <CardContent>
          {routes.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">
              Aún no hay colaboradores inscritos en rutas de autoaprendizaje.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Ruta</TableHead>
                  <TableHead className="text-right">Inscritos</TableHead>
                  <TableHead>Avance promedio</TableHead>
                  <TableHead className="text-right">Completadas</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {routes.map(r => (
                  <TableRow key={r.program_id}>
                    <TableCell className="font-medium">{r.program_name}</TableCell>
                    <TableCell className="text-right">{r.enrolled}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={r.avg_progress} className="h-2 w-28" />
                        <span className="text-xs text-muted-foreground">{r.avg_progress}%</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right">{r.completed}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Por persona */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Avance por colaborador</CardTitle>
          <CardDescription>Progreso de contenido obligatorio y capítulos atrasados</CardDescription>
        </CardHeader>
        <CardContent>
          {users.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Sin datos para mostrar.</p>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Colaborador</TableHead>
                    <TableHead>Ruta</TableHead>
                    <TableHead>Inicio del plan</TableHead>
                    <TableHead>Avance obligatorio</TableHead>
                    <TableHead className="text-right">Atrasados</TableHead>
                    <TableHead>Última actividad</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={`${u.user_id}-${u.program_id}`}>
                      <TableCell>
                        <div className="font-medium">{u.full_name ?? 'Sin nombre'}</div>
                        <div className="text-xs text-muted-foreground">{u.email}</div>
                      </TableCell>
                      <TableCell>{u.program_name}</TableCell>
                      <TableCell className="text-sm">{fmtDate(u.plan_start_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress value={u.progress_pct} className="h-2 w-24" />
                          <span className="text-xs text-muted-foreground">
                            {u.done_mandatory}/{u.total_mandatory}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        {u.overdue_chapters > 0 ? (
                          <Badge variant="destructive">{u.overdue_chapters}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">{fmtDate(u.last_activity)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Interesados */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="text-lg">Interesados del equipo</CardTitle>
            <CardDescription>Colaboradores que registraron interés en rutas con acompañamiento</CardDescription>
          </div>
          <Button variant="outline" size="sm" onClick={handleExportInterests} disabled={interests.length === 0}>
            <Download className="h-4 w-4 mr-2" />
            Exportar CSV
          </Button>
        </CardHeader>
        <CardContent>
          {interests.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Aún no hay registros de interés.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Correo</TableHead>
                  <TableHead>Ruta</TableHead>
                  <TableHead>Fecha</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {interests.map(i => (
                  <TableRow key={i.id}>
                    <TableCell>{i.full_name ?? '—'}</TableCell>
                    <TableCell className="text-sm">{i.email}</TableCell>
                    <TableCell>{i.program_name ?? '—'}</TableCell>
                    <TableCell className="text-sm">{fmtDate(i.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
